import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import GoTo from "../components/GoTo";
import { useAuth } from "../context/AuthContext";
import { createPaymobPayment } from "../utils/paymobApi";
import { useLanguage } from "../context/LanguageContext";

function OrdersDetailsPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const { token, logout, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("items");
  const [isPaying, setIsPaying] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/account-login");
      return;
    }

    // Get order data from location state (passed from orders list)
    if (location.state?.order) {
      setOrder(location.state.order);
      setLoading(false);
    } else {
      // If no order data in state, show error
      setError(
        language === "ar"
          ? "لم يتم العثور على بيانات الطلب. يرجى العودة إلى قائمة الطلبات والمحاولة مرة أخرى."
          : "Order data not found. Please go back to orders list and try again."
      );
      setLoading(false);
    }
  }, [token, navigate, location.state]);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await logout();
    } finally {
      navigate("/account-login");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return language === "ar" ? "غير متاح" : "N/A";
    try {
      // Handle different date formats from backend
      let date;
      if (typeof dateString === "string") {
        // If it's already a formatted string, try to parse it
        date = new Date(dateString);
        if (isNaN(date.getTime())) {
          // If parsing fails, return the original string
          return dateString;
        }
      } else {
        date = new Date(dateString);
      }

      return date.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString || (language === "ar" ? "غير متاح" : "N/A");
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0.00 EGP";
    return `${parseFloat(amount).toFixed(2)} EGP`;
  };

  const getStatusBadge = (state) => {
    const statusMap = language === "ar"
      ? {
          draft: "مسودة",
          sent: "تم الإرسال",
          sale: "مؤكد",
          done: "منجز",
          cancel: "ملغي",
          "on hold": "قيد الانتظار",
        }
      : {
          draft: "Draft",
          sent: "Sent",
          sale: "Confirmed",
          done: "Done",
          cancel: "Cancelled",
          "on hold": "On Hold",
        };
    return statusMap[state] || state || (language === "ar" ? "غير معروف" : "Unknown");
  };

  const getStatusClass = (state) => {
    const classMap = {
      draft: "badge-warning",
      sent: "badge-info",
      sale: "badge-success",
      done: "badge-success",
      cancel: "badge-danger",
      "on hold": "badge-warning",
    };
    return classMap[state] || "badge-secondary";
  };

  const getPaymentStatus = (isPaid) => {
    return isPaid ? (language === "ar" ? "مدفوع" : "Paid") : (language === "ar" ? "قيد الانتظار" : "Pending");
  };

  const getPaymentClass = (isPaid) => {
    return isPaid ? "text-success" : "text-warning";
  };

  const handlePayNow = async () => {
    if (!order || !user) return;

    setIsPaying(true);
    setError("");

    try {
      const userName = user?.name || user?.user_name || "";
      const splittedUserName = userName.split(" ");
      const firstName = splittedUserName[0] || "";
      const lastName = splittedUserName.length > 1 ? splittedUserName[1] : "";

      const paymobUrl = await createPaymobPayment(
        order.id,
        order.total || 0,
        firstName,
        lastName,
        user?.phone || user?.mobile || "",
        user?.email || ""
      );

      // Redirect to Paymob unified checkout
      window.location.href = paymobUrl;
    } catch (e) {
      setError(e?.message || (language === "ar" ? "فشل بدء الدفع" : "Failed to initiate payment"));
    } finally {
      setIsPaying(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <PageTitle title={language === "ar" ? "تفاصيل الطلب" : "Order Details"} />
        <section className="flat-spacing-11">
          <div className="container">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">{language === "ar" ? "جاري التحميل..." : "Loading..."}</span>
              </div>
              <p className="mt-3">{language === "ar" ? "جاري تحميل تفاصيل الطلب..." : "Loading order details..."}</p>
            </div>
          </div>
        </section>
        <Footer />
        <GoTo />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Header />
        <PageTitle title={language === "ar" ? "تفاصيل الطلب" : "Order Details"} />
        <section className="flat-spacing-11">
          <div className="container">
            <div className="alert alert-danger">
              {error}
              <Link
                to="/account-orders"
                className="btn btn-sm btn-outline-danger ms-2"
              >
                {language === "ar" ? "العودة إلى الطلبات" : "Back to Orders"}
              </Link>
            </div>
          </div>
        </section>
        <Footer />
        <GoTo />
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        <Header />
        <PageTitle title={language === "ar" ? "تفاصيل الطلب" : "Order Details"} />
        <section className="flat-spacing-11">
          <div className="container">
            <div className="text-center py-5">
              <h5>{language === "ar" ? "الطلب غير موجود" : "Order not found"}</h5>
              <p>{language === "ar" ? "الطلب الذي تبحث عنه غير موجود." : "The order you're looking for doesn't exist."}</p>
              <Link to="/account-orders" className="btn btn-primary">
                {language === "ar" ? "العودة إلى الطلبات" : "Back to Orders"}
              </Link>
            </div>
          </div>
        </section>
        <Footer />
        <GoTo />
      </div>
    );
  }

  return (
    <div>
      <Header />
      <PageTitle title={language === "ar" ? "تفاصيل الطلب" : "Order Details"} />
      {/* page-cart */}
      <section className="flat-spacing-11">
        <div className="container">
          <div className="wd-form-order">
            <div className="order-head">
              <figure className="img-product">
                <img
                  src={
                    order.lines?.[0]?.product_image_url ||
                    "/assets/images/products/default.jpg"
                  }
                  alt="product"
                />
              </figure>
              <div className="content">
                <div className={`badge ${getStatusClass(order.state)}`}>
                  {getStatusBadge(order.state)}
                </div>
                <h6 className="mt-8 fw-5">
                  {language === "ar" ? "الطلب" : "Order"} {order.name || `#${order.id}`}
                </h6>
              </div>
            </div>
            <div className="tf-grid-layout md-col-2 gap-15">
              <div className="item">
                <div className="text-2 text_black-2">{language === "ar" ? "تاريخ الطلب" : "Order Date"}</div>
                <div className="text-2 mt_4 fw-6">{formatDate(order.date)}</div>
              </div>
              <div className="item">
                <div className="text-2 text_black-2">{language === "ar" ? "المبلغ الإجمالي" : "Total Amount"}</div>
                <div className="text-2 mt_4 fw-6">
                  {formatCurrency(order.total)}
                </div>
              </div>
              <div className="item">
                <div className="text-2 text_black-2">{language === "ar" ? "عدد العناصر" : "Items Count"}</div>
                <div className="text-2 mt_4 fw-6">
                  {order.lines?.length || 0} {(order.lines?.length || 0) === 1 
                    ? (language === "ar" ? "عنصر" : "item")
                    : (language === "ar" ? "عناصر" : "items")}
                </div>
              </div>
              <div className="item">
                <div className="text-2 text_black-2">{language === "ar" ? "حالة الدفع" : "Payment Status"}</div>
                <div className="text-2 mt_4 fw-6">
                  <span className={getPaymentClass(order.is_paid)}>
                    {getPaymentStatus(order.is_paid)}
                  </span>
                </div>
                {!order.is_paid && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handlePayNow}
                      disabled={isPaying}
                      className="tf-btn btn-fill animate-hover-btn radius-3"
                    >
                      <span>{isPaying ? (language === "ar" ? "جاري المعالجة..." : "Processing...") : (language === "ar" ? "ادفع الآن" : "Pay Now")}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="widget-tabs style-has-border widget-order-tab">
              <ul className="widget-menu-tab">
                <li
                  className={`item-title ${
                    activeTab === "items" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("items")}
                  style={{ cursor: "pointer" }}
                >
                  <span className="inner">{language === "ar" ? "تفاصيل العناصر" : "Item Details"}</span>
                </li>
                <li
                  className={`item-title ${
                    activeTab === "history" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("history")}
                  style={{ cursor: "pointer" }}
                >
                  <span className="inner">{language === "ar" ? "تاريخ الطلب" : "Order History"}</span>
                </li>
              </ul>
              <div className="widget-content-tab">
                <div
                  className={`widget-content-inner ${
                    activeTab === "items" ? "active" : ""
                  }`}
                >
                  {order.lines && order.lines.length > 0 ? (
                    <>
                      {order.lines.map((item, index) => (
                        <div key={index} className="order-head mb-3">
                          <figure className="img-product">
                            <img
                              src={
                                item.product_image_url ||
                                "/assets/images/products/default.jpg"
                              }
                              alt="product"
                            />
                          </figure>
                          <div className="content">
                            <div className="text-2 fw-6">
                              {item.product_name || (language === "ar" ? "المنتج" : "Product")}
                            </div>
                            <div className="mt_4">
                              <span className="fw-6">{language === "ar" ? "السعر:" : "Price :"}</span>{" "}
                              {formatCurrency(item.price)}
                            </div>
                            <div className="mt_4">
                              <span className="fw-6">{language === "ar" ? "الكمية:" : "Quantity :"}</span>{" "}
                              {item.qty || 1}
                            </div>
                            <div className="mt_4">
                              <span className="fw-6">{language === "ar" ? "المجموع الفرعي:" : "Subtotal :"}</span>{" "}
                              {formatCurrency(item.price_subtotal)}
                            </div>
                            {item.discount > 0 && (
                              <div className="mt_4">
                                <span className="fw-6">{language === "ar" ? "الخصم:" : "Discount :"}</span>{" "}
                                {item.list_discounts || `${item.discount}%`}
                              </div>
                            )}
                            <div className="mt_4">
                              <span className="fw-6">{language === "ar" ? "الفئة:" : "Category :"}</span>{" "}
                              {item.categ_name || (language === "ar" ? "غير متاح" : "N/A")}
                            </div>
                            <div className="mt_4">
                              <span className="fw-6">{language === "ar" ? "الموديل:" : "Model :"}</span>{" "}
                              {item.model_name || (language === "ar" ? "غير متاح" : "N/A")}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="mt-4 pt-3 border-top">
                        <ul>
                          <li className="d-flex justify-content-between text-2">
                            <span>{language === "ar" ? "المجموع الفرعي" : "Subtotal"}</span>
                            <span className="fw-6">
                              {formatCurrency(order.total / 1.14)}
                            </span>
                          </li>
                          <li className="d-flex justify-content-between text-2 mt_4">
                            <span>{language === "ar" ? "الضريبة (14%)" : "Tax (14%)"}</span>
                            <span className="fw-6">
                              {formatCurrency(order.total - order.total / 1.14)}
                            </span>
                          </li>
                          <li className="d-flex justify-content-between text-2 mt_8">
                            <span className="fw-6">{language === "ar" ? "إجمالي الطلب" : "Order Total"}</span>
                            <span className="fw-6">
                              {formatCurrency(order.total)}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-muted">
                        {language === "ar" ? "لم يتم العثور على عناصر في هذا الطلب." : "No items found in this order."}
                      </p>
                    </div>
                  )}
                </div>
                <div
                  className={`widget-content-inner ${
                    activeTab === "history" ? "active" : ""
                  }`}
                >
                  <div className="widget-timeline">
                    <ul className="timeline">
                      <li>
                        <div className="timeline-badge success"></div>
                        <div className="timeline-box">
                          <div className="timeline-panel">
                            <div className="text-2 fw-6">{language === "ar" ? "تم تقديم الطلب" : "Order Placed"}</div>
                            <span>{formatDate(order.date)}</span>
                          </div>
                          <p>
                            <strong>{language === "ar" ? "رقم الطلب:" : "Order Number: "}</strong>
                            {order.name || `#${order.id}`}
                          </p>
                          <p>
                            <strong>{language === "ar" ? "المبلغ الإجمالي:" : "Total Amount: "}</strong>
                            {formatCurrency(order.total)}
                          </p>
                        </div>
                      </li>
                      <li>
                        <div className="timeline-badge success"></div>
                        <div className="timeline-box">
                          <div className="timeline-panel">
                            <div className="text-2 fw-6">{language === "ar" ? "تم دفع الطلب" : "Order Paid"}</div>
                            <span>{formatDate(order.date)}</span>
                          </div>
                          <p>
                            <strong>{language === "ar" ? "حالة الدفع:" : "Payment Status: "}</strong>
                            {order.is_paid
                              ? (language === "ar" ? "تم استلام الدفع بنجاح" : "Payment received successfully")
                              : (language === "ar" ? "الدفع قيد الانتظار" : "Payment pending")}
                          </p>
                          <p>
                            <strong>{language === "ar" ? "المبلغ المدفوع:" : "Amount Paid: "}</strong>
                            {formatCurrency(order.total)}
                          </p>
                        </div>
                      </li>
                      {order.state === "done" && (
                        <li>
                          <div className="timeline-badge success"></div>
                          <div className="timeline-box">
                            <div className="timeline-panel">
                              <div className="text-2 fw-6">{language === "ar" ? "اكتمل الطلب" : "Order Completed"}</div>
                              <span>{formatDate(order.date)}</span>
                            </div>
                            <p>
                              <strong>{language === "ar" ? "الحالة:" : "Status: "}</strong>
                              {language === "ar" ? "تم إكمال الطلب بنجاح" : "Order has been completed successfully"}
                            </p>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <GoTo />
    </div>
  );
}

export default OrdersDetailsPage;
